import {
  type Task,
  TaskAssigneeType,
  type User as APIUser,
  type EmployeeMetadata,
} from 'src/api/generated';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';

export type User = APIUser;
export type Employee = EmployeeMetadata;

/**
 * Parse assignee value in format "type:id" or "type-id"
 */
export const parseAssigneeValue = (
  value: string,
  separator = ':'
): { type: string; id: string } => {
  const [type, id] = value.split(separator);
  return { type, id };
};

/**
 * Create assignee value in format "type:id" or "type-id"
 */
export const createAssigneeValue = (
  type: string,
  id: string,
  separator = ':'
): string => {
  return `${type}${separator}${id}`;
};

/**
 * Get assignee display name from task
 */
export const getAssigneeDisplay = (
  task: Partial<Pick<Task, 'assignedTo'>>,
  users: User[],
  employees: Employee[]
): string => {
  if (task.assignedTo?.type === TaskAssigneeType.USER) {
    const user = users.find((u) => u.id.toString() === task.assignedTo?.id);
    return user
      ? formatFullName(user.firstName, user.lastName)
      : // TODO: check when implemnenting i18n
        'Usuario sin nombre';
  } else if (task.assignedTo?.type === TaskAssigneeType.AI_EMPLOYEE) {
    const employee = employees.find(
      (e) => e.employeeId === task.assignedTo?.id
    );
    // TODO: check when implemnenting i18n
    return employee ? getEmployeeDisplayName(employee) : 'Agente IA sin nombre';
  }
  // TODO: check when implemnenting i18n
  return 'Sin asignar';
};

/**
 * Convert assignee type to API enum
 */
export const getAssigneeTypeEnum = (type: string): TaskAssigneeType => {
  return type === 'user' ? TaskAssigneeType.USER : TaskAssigneeType.AI_EMPLOYEE;
};

/**
 * Get user display name for dropdown
 */
export const getUserDisplayName = (user: User): string => {
  return formatFullName(user.firstName, user.lastName);
};

/**
 * Get employee display name for dropdown
 */
export const getEmployeeDisplayName = (employee: Employee): string => {
  // TODO: check when implemnenting i18n
  if (!employee.name) return 'Agente IA sin nombre';
  return employee.name;
};

/**
 * Check if assignee is selected for user
 */
export const isUserSelected = (user: User, currentTask?: Task): boolean => {
  return (
    currentTask?.assignedTo.type === TaskAssigneeType.USER &&
    currentTask.assignedTo.id === user.id.toString()
  );
};

/**
 * Check if assignee is selected for employee
 */
export const isEmployeeSelected = (
  employee: Employee,
  currentTask?: Task
): boolean => {
  return (
    currentTask?.assignedTo.type === TaskAssigneeType.AI_EMPLOYEE &&
    currentTask.assignedTo.id === employee.employeeId
  );
};
